import type { ContinuityMeasure } from '@/api/continuity-snapshot'
import type { MetricChange } from '@/api/snapshot'
import type { ClinicalIndicatorId, ContinuityMetricId } from '@/types/intelligence'
import { formatDay } from './metric-format'

type Translate = (key: string, named?: Record<string, unknown>, plural?: number) => string

/**
 * How Care Quality & Continuity writes its figures, in the reader's language
 * (P2 §2.2): a share as a whole percentage, its move in percentage points to
 * one decimal, a count with its thousands separator. Numbers are formatted
 * here and interpolated, never put into a string as text (i18n-strings skill).
 */

/** The strings each measure's name and definition live under (`overview.continuity.measures.<key>`). */
export const CONTINUITY_KEYS: Record<ContinuityMetricId | ClinicalIndicatorId, string> = {
  'METRIC-CONT-COHORT-ID': 'cohortId',
  'METRIC-CONT-RISK-PROFILES': 'riskProfiles',
  'METRIC-CONT-CAREPLAN-ACT': 'careplanAct',
  'METRIC-CONT-SCHEDULED-FOLLOWUP': 'scheduledFollowup',
  'METRIC-CONT-PT-CONNECT': 'ptConnect',
  'METRIC-CONT-INTERENCOUNTER-REACH': 'interencounterReach',
  'METRIC-CONT-90D-RETENTION': 'retention90d',
  'METRIC-CONT-DENSE-PATIENT-MONTHS': 'densePatientMonths',
  'METRIC-CONT-LOOP-CLOSURE': 'loopClosure',
  'METRIC-CONT-ABNORMAL-REVIEW': 'abnormalReview',
  'IND-CARE-DIAB-HBA1C': 'hba1c',
  'IND-CARE-HTN-BP-CONTROL': 'bpControl',
  'IND-CARE-KDIGO-RENAL': 'kdigoRenal',
  'IND-CARE-DIAB-FOOT-EXAM': 'footExam',
  'IND-CARE-NOTE-WORKFLOW-AUDIT': 'noteAudit',
}

const percent = (rate: number, locale: string, decimals: 0 | 1): string =>
  new Intl.NumberFormat(locale, { style: 'percent', minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(rate)

/**
 * A share, 0 to 1, as a whole percentage ("87%"), or to one decimal ("8.4%").
 * A whole percentage never says none or all of a share that is neither: 2 of
 * 812 is "0.2%", not "0%" beside its count and a move in points, and closer
 * still to either end it is "<0.1%" or ">99.9%".
 */
export const formatRate = (rate: number, locale: string, decimals: 0 | 1 = 0): string => {
  const whole = Math.round(rate * 100)
  if (decimals === 1 || rate <= 0 || rate >= 1 || (whole > 0 && whole < 100)) return percent(rate, locale, decimals)
  if (rate < 0.001) return `<${percent(0.001, locale, 1)}`
  if (rate > 0.999) return `>${percent(0.999, locale, 1)}`
  return percent(rate, locale, 1)
}

/**
 * A share then and now, as whole percentages, unless they would read the
 * same: a move counts once it is 5% of where it started, so 8.0% to 8.4% is
 * one, and "from 8% to 8%" would say it was not. Then both get one decimal.
 */
export const formatRates = (from: number, to: number, locale: string): [string, string] => {
  const whole: [string, string] = [formatRate(from, locale), formatRate(to, locale)]
  return whole[0] === whole[1] ? [formatRate(from, locale, 1), formatRate(to, locale, 1)] : whole
}

/** A count with its thousands separator. */
export const formatCount = (value: number, locale: string): string => new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value)

/**
 * A move: a rate's in percentage points to one decimal, as the server measures
 * it and Ask Elfie writes it ("+0.4 pts", "−4 pts"), so a move that counts is
 * never rounded to "0 pts"; a count's as a signed percentage, as the
 * Operations cards write counts.
 */
export const formatMove = (measure: Pick<ContinuityMeasure, 'rate' | 'change'> & { change: MetricChange }, t: Translate, locale: string): string => {
  const { change } = measure
  if (measure.rate !== null) {
    const points = new Intl.NumberFormat(locale, { maximumFractionDigits: 1, signDisplay: 'exceptZero' }).format(change.absolute)
    return t('overview.continuity.points', { value: points })
  }
  if (change.relative === null) {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0, signDisplay: 'exceptZero' }).format(change.absolute)
  }
  return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0, signDisplay: 'exceptZero' }).format(change.relative)
}

/**
 * When every figure is taken and what it is compared with, in one line: now,
 * against the same time on the baseline's last day, while the period runs on;
 * otherwise the end of its last day (Dao, 2 Oct 2026). The Overview, a saved
 * report and its CSV file say it alike.
 */
export const continuityAsOf = (period: { last: string; baselineLast: string }, ongoing: boolean, t: Translate, locale: string): string =>
  ongoing
    ? t('overview.continuity.asOf.now', { day: formatDay(period.baselineLast, locale) })
    : t('overview.continuity.asOf.end', { day: formatDay(period.last, locale), before: formatDay(period.baselineLast, locale) })

/** Better or worse, for a measure where more is better; nothing for one that held steady. */
export const verdictOfMeasure = (measure: Pick<ContinuityMeasure, 'change'>): 'BETTER' | 'WORSE' | null =>
  !measure.change || measure.change.direction === 'STEADY' ? null : measure.change.direction === 'UP' ? 'BETTER' : 'WORSE'

/** What limits a measure, one sentence each, in the reader's language. */
export const continuityNoteText = (note: ContinuityMeasure['notes'][number], t: Translate, locale: string, formatDay: (day: string) => string): string => {
  switch (note.code) {
    case 'TOO_FEW':
      return t('overview.continuity.notes.TOO_FEW', { minimum: note.minimum })
    case 'INPUTS_NOT_RECORDED':
      return t('overview.continuity.notes.INPUTS_NOT_RECORDED', {
        inputs: new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(note.inputs.map((input) => t(`coverage.inputs.${input}.needs`))),
      })
    case 'MEASURED_OF':
      return t('overview.continuity.notes.MEASURED_OF', { measured: formatCount(note.measured, locale), of: formatCount(note.of, locale) })
    case 'NOT_RECORDED_BEFORE':
      return t('overview.notes.NOT_RECORDED_BEFORE', { day: formatDay(note.day) })
    case 'NOT_RECORDED':
      return note.day ? t('overview.notes.NOT_RECORDED', { day: formatDay(note.day) }) : t('overview.notes.NOT_RECORDED_YET')
    case 'RECORDS_NOT_CONNECTED':
    case 'LAB_ORDERS_NOT_RECORDED':
      return t(`overview.continuity.notes.${note.code}`)
    default:
      // The data cannot be read at all: the Overview's own words for why.
      return t(`overview.notes.${note.code}`)
  }
}
